// @givmo/step-up — what a host supplies so the one "confirm it's you" prompt can run (the seam
// agreed with Account Security, 2026-10-02 13:00 PDT).
//
// The package owns the gate, the prompt and their behaviour; it owns NO sign-in. A host (an app
// with a Firebase session) answers for the person's own way back in, the second-factor step, the
// forced token refresh, and the words a failed sign-in shows. Nothing here imports Firebase.
//
// ══ THE PASSKEY SLOT (condition 2; Account Security's arm, AS-WEB10) ═══════════════════════════
//
// `confirm()` re-confirms the person with one of THEIR OWN passkeys and rejects with a typed
// reason — `cancelled_or_failed` (the ceremony was dismissed or failed: the prompt stays, saying
// so) or `none_registered` (no passkey after all: the prompt moves to the person's own way in, or,
// where the passkey was the only way, the act fails with that `StepUpPasskeyFailure` so the caller
// can offer to set one up). Anything else shows the host's words for it.
//
// THE SLOT'S OBLIGATIONS, for any host that fills it (Account Security owns them; D-AS-17):
//   - the account-bound route ONLY (`/users/me/passkeys/reauthentication*`, `@givmo/auth`'s
//     `reconfirmWithPasskey`), never the unbound passkey sign-in (`signInWithPasskey`,
//     `adminSignInWithPasskey`): whoever asserts there becomes the signed-in user. `@givmo/auth`'s
//     `index.test.ts` fails any module that fills a slot and names either;
//   - the minted token's account checked against the signed-in one BEFORE `signInWithCustomToken`;
//   - exactly one exchange, then a forced refresh of the new session's ID token;
//   - the suite session login re-run for the new session, so later handoffs carry its freshness.
//
// ══ ACCEPTED WAYS IN ARE PER PROMPT, AND NAMED ONE BY ONE (condition 1; D-AS-24/25/26) ══════════
//
// Each prompt says which ways in it accepts (`StepUpPromptOptions.accepts`), by name: the passkey,
// the password, or a re-sign-in with Google, Apple or Microsoft. The default is all of them, so a
// person meets the prompt with whatever they hold. The refusal's CODE narrows them further
// (`stepUpRefusalWays`): a passkey confirmation refusal (`passkey_confirmation_required`) opens a
// passkey-only prompt whatever the call names, and a module's own refusal codes
// (`StepUpGateOptions.refusalCodes`, e.g. Board's signing refusal) are answered like
// `step_up_required`. When Account Security's per-environment switch (AS-PWL-10) retires the
// password fallback, prompts stop naming `'password'`: a change of list, never of this contract. An
// emailed or texted code is not a way in here at all: it never satisfies a stricter level.
//
// The host lists EVERY way back in the person holds (`waysIn`), in its order of preference, and a
// prompt takes the first it accepts. Handing over only the preferred one would strand a person with
// a password and a Google account the day the password stops counting.

import { createContext, useContext, type ReactNode } from 'react';

/** A way in a prompt can name. */
export type StepUpWayIn = 'passkey' | 'password' | 'google' | 'apple' | 'microsoft';

/** The re-sign-in providers, by the name a prompt accepts them under. */
export type StepUpProviderWayIn = Extract<StepUpWayIn, 'google' | 'apple' | 'microsoft'>;

/** Every way in: what a prompt accepts when it names none. */
export const STEP_UP_WAYS_IN: readonly StepUpWayIn[] = [
  'passkey',
  'password',
  'google',
  'apple',
  'microsoft',
];

/** One way back in the person holds (the passkey is the slot's, below). */
export type StepUpOwnWayIn =
  | { readonly kind: 'password' }
  | {
      readonly kind: 'provider';
      /** The name a prompt accepts it under. */
      readonly wayIn: StepUpProviderWayIn;
      /** What the host's `reauthenticateWithProvider` takes. */
      readonly id: string;
      readonly label: string;
    };

/** The name a prompt accepts this way in under. */
export function stepUpWayInName(way: StepUpOwnWayIn): StepUpWayIn {
  return way.kind === 'password' ? 'password' : way.wayIn;
}

/**
 * The first of the person's ways in that this prompt accepts, or `undefined`: an honest dead-end,
 * never a prompt that cannot succeed.
 */
export function acceptedStepUpWayIn(
  waysIn: readonly StepUpOwnWayIn[],
  accepts: readonly StepUpWayIn[],
): StepUpOwnWayIn | undefined {
  return waysIn.find((way) => accepts.includes(stepUpWayInName(way)));
}

/** Why a passkey re-confirmation did not complete (condition 2). */
export type StepUpPasskeyFailureReason = 'cancelled_or_failed' | 'none_registered';

export class StepUpPasskeyFailure extends Error {
  readonly reason: StepUpPasskeyFailureReason;
  /**
   * The server's refusal no way here could answer, when the gate failed the act on one (the
   * passkey-only dead end): a caller that knows the act may read its own words off it — the add
   * flow does, for rule 1's stale first-passkey refusal (AS-PWL-2e). `undefined` otherwise.
   */
  readonly refusal: unknown;

  constructor(reason: StepUpPasskeyFailureReason, refusal?: unknown) {
    super(`Passkey confirmation did not complete (${reason}).`);
    this.name = 'StepUpPasskeyFailure';
    this.reason = reason;
    this.refusal = refusal;
    Object.setPrototypeOf(this, StepUpPasskeyFailure.prototype);
  }
}

/** The passkey arm's slot, filled by Account Security's account-bound re-confirmation (AS-WEB10). */
export interface StepUpPasskeySlot {
  /** Whether the person holds a passkey this prompt could use. */
  holdsPasskey: boolean;
  /** Re-confirm with the passkey. Rejects with a {@link StepUpPasskeyFailure}. */
  confirm: () => Promise<void>;
  /**
   * Where "Lost your passkey?" leads (the passkey reset). The link is drawn only on a prompt where
   * the passkey is the ONLY way through, and only when a host supplies this or `renderLost` (which
   * wins: the reset opens inside the sheet): absent both, it is hidden.
   */
  lost?: () => void;
}

/**
 * The sheet's own code step, for a host that can complete a pending second factor with a code.
 * Supplied, the sheet draws the approved six-box step (one shared look and wording for every host);
 * absent, it hands the challenge to the host's `renderSecondFactor`, as before.
 */
export interface StepUpCodeStep {
  /** Complete the pending challenge (`secondFactorOf`'s) with a code. Rejects when refused. */
  resolve: (challenge: unknown, code: string) => Promise<void>;
  /** Whether a refusal was the CODE's (wrong, expired or already used): the boxes clear and say so. */
  isRejectedCode: (thrown: unknown) => boolean;
}

/** Which factor a lost link names: "Lost your passkey?" or "Lost your authenticator?". */
export type StepUpLostFactor = 'passkey' | 'authenticator';

/**
 * What a lost factor means for ONE act, said in the lost view (D-AS-21's B2d-lost; AS-WEB18): a
 * module's row supplies them (`StepUpCopyRow.lostLines`), as Board's signing rows say what signing
 * waits on. Every line is optional: one left out is the host view's own (the consoles' words).
 */
export interface StepUpLostLines {
  /**
   * The line under the view's title: what the person can still do while the factor is lost, and
   * what waits. The authenticator's view shows it above its two ways out; the passkey reset, above
   * its first step.
   */
  line?: string;
  /** Under "Use a recovery code" (the authenticator's view). */
  recoveryCode?: string;
  /** Under "Email me a reset" (the authenticator's view). */
  emailReset?: string;
  /**
   * After a recovery code removed the authenticator, under the result's title: what happens next
   * (the authenticator's view).
   */
  recoveryCodeResult?: string;
}

/** One of the authenticator's ways out (B2d-lost): a saved recovery code, or the emailed reset. */
export type StepUpLostWayOut = 'recoveryCode' | 'emailReset';

/** What a host's lost view is handed (`StepUpHostAdapter.renderLost`). */
export interface StepUpLostProps {
  /** The factor the person can't use. */
  factor: StepUpLostFactor;
  /**
   * Back to the "Confirm it's you" sheet. Given when the view is drawn INSIDE the sheet; absent
   * where a surface opens the view on its own (the Sign-in & security card's "Can't use a
   * passkey?"), whose only way out is `onClose`.
   */
  onBack?: () => void;
  /** Close it. Inside the sheet, the act fails as cancelled. */
  onClose: () => void;
  /** The act's own lines for this view (its row's `lostLines`); absent, the view's own (AS-WEB18). */
  lines?: StepUpLostLines;
  /**
   * Open on one way out rather than the authenticator's choice of both: the Sign-in & security card's
   * "Finish the reset" opens the emailed reset itself (AS-WEB17). Absent, the view opens as a lost
   * link leads.
   */
  wayOut?: StepUpLostWayOut;
}

/** What the second-factor step is handed: the opaque pending challenge and its words. */
export interface StepUpSecondFactorProps {
  challenge: unknown;
  title: string;
  /** The step's line, given the factor's display name where the host knows it. */
  description: (factorDisplayName: string | null) => string;
  cancelLabel: string;
  /** The second factor is complete: the re-authentication is done. */
  onResolved: () => void;
  /** Back to the person's own way in. */
  onCancel: () => void;
}

/** The host's half of the seam. */
export interface StepUpHostAdapter {
  /**
   * Every way back in the person holds, from the account's linked sign-ins, in the host's order of
   * preference. Empty when they hold none a prompt could run.
   */
  waysIn: readonly StepUpOwnWayIn[];
  reauthenticateWithPassword: (password: string) => Promise<void>;
  reauthenticateWithProvider: (providerId: string) => Promise<void>;
  /**
   * Force a fresh ID token for the LIVE session. The gate calls it between the refused attempt and
   * its one retry: a cached token would carry the very `auth_time` that was refused.
   */
  refreshIdToken: () => Promise<unknown>;
  /** The pending second-factor challenge a re-authentication raised, or `null` when it raised none. */
  secondFactorOf: (thrown: unknown) => unknown;
  /** The host's own second-factor step (its code prompt), completing the same re-authentication. */
  renderSecondFactor: (props: StepUpSecondFactorProps) => ReactNode;
  /** The words a failed re-authentication shows, or `null` for one to show nothing (a cancel). */
  describeError: (thrown: unknown) => string | null;
  /** Account Security's passkey arm: absent, the prompt offers no passkey. */
  passkey?: StepUpPasskeySlot;
  /**
   * AS-PWL-2e — end this session and load this page again, signed out, so the page's own guard sends
   * it to ITS sign-in door and brings it back here once signed in again (the host names no door: a
   * Board director's page has its own). For a cure only a NEW sign-in gives (rule 1's first passkey
   * on a sign-in older than its window): the passkey offer draws "Sign in again" with it. It must
   * release the suite session too, so no silent handoff brings the old sign-in back (law 8: a
   * handoff's evidence is the origin's). Absent, the offer shows the refusal's words alone.
   */
  signInAgain?: () => void;
  /**
   * Whether the account holds an authenticator app, from the server's own record
   * (`GET /users/me/security`'s `mfa.totp_enrolled`); `undefined` while that is not known (the read
   * is not served, has not answered, or — for a host that reads on demand — has not been made:
   * `prepare`). A re-sign-in on such an account owes a code, so the sheet's credential step reads
   * "Continue" rather than its confirm; and a module that must choose between this prompt and a
   * factor setup offer (Board's Sign) reads the same source the sheet does, after `prepare`.
   */
  holdsAuthenticator?: boolean;
  /**
   * Read what the account holds NOW, for a host whose facts (`passkey`, `holdsAuthenticator`,
   * `renderLost`) come from reads it makes only on demand (AS-WEB15: a console mounts its host once
   * at the shell, and a host that read on render would read on every page and every window focus).
   * The gate calls it when a refusal is about to open a prompt, and opens the sheet once it has
   * settled (or after `STEP_UP_PREPARE_WAIT_MS`), so the sheet chooses its arms, and a passkey-only
   * prompt for an account with none fails at once, on the account's answer. A module that decides
   * before any prompt calls it too. It resolves once the adapter it fed has re-rendered, and never
   * rejects: a failed read leaves the answers as they were. Absent, the facts are the host's own and
   * the gate opens at once, as before.
   */
  prepare?: () => Promise<void>;
  /**
   * A `prepare` read is on the wire: until it settles, the facts are not yet the account's answer.
   * (The caller's side of the same vocabulary is the gate's `prompting`: whether a run waits on the
   * person at a sheet. See `StepUpGate`.)
   */
  preparing?: boolean;
  /** The sheet's own code step (see {@link StepUpCodeStep}); absent, `renderSecondFactor` draws it. */
  codeStep?: StepUpCodeStep;
  /**
   * Where "Lost your authenticator?" leads. Drawn only where the authenticator is the only way
   * through (no passkey this prompt could use), and only when a host supplies it.
   */
  lostAuthenticator?: () => void;
  /**
   * Where a lost link leads, drawn INSIDE the same sheet (D-AS-21's B2d-lost pattern): the host's
   * account reset (AS-WEB13). Supplied, "Lost your passkey?" and "Lost your authenticator?" render
   * wherever their factor is the only way through, and choosing one swaps the sheet's content for
   * this view: its Back returns to the sheet, and closing it fails the act as cancelled. Absent,
   * `passkey.lost` and `lostAuthenticator` decide, as before.
   */
  renderLost?: (props: StepUpLostProps) => ReactNode;
}

/**
 * The host's half of the seam, for every gate beneath it. `StepUpHostProvider` (beside the gate,
 * since it also keeps the ONE prompt and draws the sheet: CCM-R1-STEP-UP-B, P4) provides it.
 */
export const StepUpHostContext = createContext<StepUpHostAdapter | null>(null);

/** The provided host, or `null` where none is (a gate may be handed one directly instead). */
export function useOptionalStepUpHost(): StepUpHostAdapter | null {
  return useContext(StepUpHostContext);
}
